import { GameConfig, visibleDesignHeight } from "../config/GameConfig";
import { GameNavigator } from "../core/Navigation";
import { GameStore } from "../core/GameStore";
import { ClueImages, IconKey } from "../data/ClueImages";
import { PictureQuestion, questionForLevel } from "../data/QuestionBank";
import { ReferenceScreenName, ReferenceUI } from "../ui/ReferenceUI";
import { UIFactory } from "../ui/UIFactory";
import { BaseScreen } from "./BaseScreen";
import { AdFarmSession } from "../integration/AdFarmSession";
import { MobileAccount, MobileOverview } from "../integration/AdFarmTypes";
import { RewardAdBridge } from "../integration/RewardAdBridge";
import { Theme } from "../ui/Theme";
import { AppTabBar } from "../ui/AppTabBar";
import { BackendBalanceChip } from "../ui/BackendBalanceChip";
import { GameAudio } from "../audio/GameAudio";
import { FestiveMotion } from "../ui/FestiveMotion";
import { SettingsOverlay } from "../ui/SettingsOverlay";

abstract class ReferenceBaseScreen extends BaseScreen {
    protected showReference(name: ReferenceScreenName): void {
        this.addChild(ReferenceUI.createScreen(name));
    }

    protected hit(
        screen: ReferenceScreenName,
        x: number,
        y: number,
        width: number,
        height: number,
        onClick: () => void,
        name: string,
    ): void {
        ReferenceUI.addHit(this, screen, x, y, width, height, onClick, name);
    }

    protected wrapGameplayContent(nav: GameNavigator): Laya.Box {
        const content = new Laya.Box();
        content.name = "gameplay_safe_content";
        content.width = GameConfig.DESIGN_WIDTH;
        content.height = GameConfig.DESIGN_HEIGHT;
        while (this.numChildren > 0) content.addChild(this.getChildAt(0));

        const availableHeight = visibleDesignHeight() - 112;
        const scale = Math.min(1, availableHeight / GameConfig.DESIGN_HEIGHT);
        content.scale(scale, scale);
        content.pos((GameConfig.DESIGN_WIDTH - GameConfig.DESIGN_WIDTH * scale) / 2, 0);

        this.graphics.drawRect(0, 0, GameConfig.DESIGN_WIDTH, visibleDesignHeight(), "#F6D59A");
        this.addChild(content);
        this.addChild(new AppTabBar(nav, "home"));
        return content;
    }
}

export class ReferenceHomeScreen extends ReferenceBaseScreen {
    private readonly session = AdFarmSession.instance;
    private readonly balanceLabel: Laya.Label;
    private mascotMotion: Laya.Box;
    private settingsOverlay: SettingsOverlay | null = null;

    constructor(nav: GameNavigator) {
        super(nav);
        this.showReference("home");
        this.addMascotMotion();
        this.hit("home", 58, 585, 340, 100, () => {
            GameAudio.playSfx("level_start", 950);
            nav.navigate("levels");
        }, "start");
        this.cleanHomeEdges();
        this.addHomeActions(nav);
        const balance = new Laya.Box();
        balance.pos(225, 36);
        balance.width = 300;
        balance.height = 58;
        UIFactory.roundRect(balance, 0, 0, 300, 54, 27, "#173F4DEB", "#F2CD76", 3);
        this.balanceLabel = UIFactory.label("后台余额 --", 23, "#FFF5C7", 290, 50, true);
        this.balanceLabel.pos(5, 2);
        balance.addChild(this.balanceLabel);
        this.addChild(balance);
        this.addSettingsButton();
        this.session.on(AdFarmSession.OVERVIEW_CHANGED, this, this.refreshBackendBalance);
        this.refreshBackendBalance(this.session.overview);
        this.addChild(new AppTabBar(nav, "home"));
    }

    private cleanHomeEdges(): void {
        const topBar = new Laya.Box();
        topBar.graphics.drawRect(0, 0, GameConfig.DESIGN_WIDTH, 104, "#258CAF");
        topBar.graphics.drawRect(0, 100, GameConfig.DESIGN_WIDTH, 4, "#F2CD7670");
        topBar.pos(0, 18);
        topBar.mouseEnabled = false;
        this.addChild(topBar);

        const actionY = visibleDesignHeight() - 112 - 98;
        const lowerCover = new Laya.Box();
        lowerCover.graphics.drawRect(0, 0, GameConfig.DESIGN_WIDTH, 150, "#3E91A4");
        lowerCover.pos(0, actionY - 18);
        lowerCover.mouseEnabled = false;
        this.addChild(lowerCover);
    }

    private addHomeActions(nav: GameNavigator): void {
        const y = visibleDesignHeight() - 112 - 88;
        const wallet = UIFactory.button("我的红包", 285, 76, () => nav.navigate("wallet"), "red", 29);
        wallet.pos(68, y);
        this.addChild(wallet);
        const rank = UIFactory.button("排行榜", 285, 76, () => nav.navigate("rank"), "gold", 29);
        rank.pos(397, y);
        this.addChild(rank);
    }

    private addSettingsButton(): void {
        const button = new Laya.Box();
        button.name = "home_settings";
        button.width = 66;
        button.height = 66;
        button.pos(662, 31);
        button.mouseEnabled = true;
        button.graphics.drawCircle(33, 37, 30, "#6F261C", "#4B1A15", 3);
        button.graphics.drawCircle(33, 32, 29, "#F0A43B", "#FFE28C", 4);
        button.graphics.drawCircle(33, 32, 21, "#E94B31", "#C7772D", 2);
        const gear = UIFactory.label("⚙", 34, "#FFF0B2", 60, 58, true);
        gear.pos(3, 3);
        gear.mouseEnabled = false;
        button.addChild(gear);
        button.on(Laya.Event.MOUSE_DOWN, button, () => Laya.Tween.to(button, { scaleX: 0.92, scaleY: 0.92 }, 70));
        button.on(Laya.Event.MOUSE_UP, button, () => Laya.Tween.to(button, { scaleX: 1, scaleY: 1 }, 100, Laya.Ease.backOut));
        button.on(Laya.Event.CLICK, button, () => {
            GameAudio.playSfx("click", 0);
            if (this.settingsOverlay) return;
            this.settingsOverlay = new SettingsOverlay(() => this.settingsOverlay = null);
            this.addChild(this.settingsOverlay);
        });
        this.addChild(button);
    }

    destroy(destroyChild = true): void {
        this.session.off(AdFarmSession.OVERVIEW_CHANGED, this, this.refreshBackendBalance);
        if (this.mascotMotion) {
            Laya.timer.clearAll(this.mascotMotion);
            Laya.Tween.clearAll(this.mascotMotion);
            for (let index = 0; index < this.mascotMotion.numChildren; index++) {
                Laya.Tween.clearAll(this.mascotMotion.getChildAt(index));
            }
        }
        super.destroy(destroyChild);
    }

    private addMascotMotion(): void {
        const sourceX = 92;
        const sourceY = 42;
        const sourceWidth = 278;
        const sourceHeight = 178;
        const rect = ReferenceUI.sourceRect("home", sourceX, sourceY, sourceWidth, sourceHeight);
        const group = new Laya.Box();
        group.name = "animated_wealth_mascot";
        group.width = rect.width;
        group.height = rect.height;
        group.pivot(rect.width / 2, rect.height);
        group.pos(rect.x + rect.width / 2, rect.y + rect.height);
        group.mouseEnabled = false;

        const halo = new Laya.Sprite();
        halo.name = "mascot_halo";
        halo.graphics.drawCircle(rect.width * 0.57, rect.height * 0.52, rect.height * 0.43, "#FFD65312", "#FFE384", 3);
        halo.alpha = 0.24;
        halo.mouseEnabled = false;
        group.addChild(halo);

        this.addChild(group);
        this.mascotMotion = group;

        const breathe = (expanded: boolean) => {
            if (!group.parent || group.destroyed) return;
            Laya.Tween.to(
                group,
                {
                    scaleX: expanded ? 1.06 : 0.96,
                    scaleY: expanded ? 1.06 : 0.96,
                    y: rect.y + rect.height + (expanded ? 5 : -6),
                },
                expanded ? 620 : 760,
                Laya.Ease.sineInOut,
                Laya.Handler.create(group, () => breathe(!expanded)),
            );
        };
        breathe(true);

        const pulseHalo = (bright: boolean) => {
            if (!halo.parent || halo.destroyed) return;
            Laya.Tween.to(
                halo,
                { alpha: bright ? 0.52 : 0.14, scaleX: bright ? 1.08 : 0.92, scaleY: bright ? 1.08 : 0.92 },
                720,
                Laya.Ease.sineInOut,
                Laya.Handler.create(halo, () => pulseHalo(!bright)),
            );
        };
        pulseHalo(true);

        for (let index = 0; index < 3; index++) this.addMascotCoin(group, rect.width, rect.height, index);
    }

    private addMascotCoin(group: Laya.Box, width: number, height: number, index: number): void {
        const coin = new Laya.Box();
        coin.name = `mascot_coin_${index}`;
        coin.width = 28;
        coin.height = 28;
        coin.graphics.drawCircle(14, 14, 12, "#F8B92E", "#FFF09B", 3);
        coin.graphics.drawCircle(14, 14, 7, "#FFD75B", "#B66A1F", 2);
        const mark = UIFactory.label("¥", 15, "#8B3B18", 28, 28, true);
        mark.mouseEnabled = false;
        coin.addChild(mark);
        coin.mouseEnabled = false;
        group.addChild(coin);

        const orbit = (opposite: boolean) => {
            if (!coin.parent || coin.destroyed) return;
            const left = index % 2 === 0;
            coin.pos(
                left ? width * (opposite ? 0.08 : 0.22) : width * (opposite ? 0.84 : 0.72),
                height * (0.22 + index * 0.18) + (opposite ? -12 : 12),
            );
            Laya.Tween.to(
                coin,
                {
                    x: left ? width * (opposite ? 0.22 : 0.08) : width * (opposite ? 0.72 : 0.84),
                    y: height * (0.22 + index * 0.18) + (opposite ? 12 : -12),
                    rotation: coin.rotation + 180,
                    alpha: opposite ? 0.48 : 0.92,
                },
                850 + index * 160,
                Laya.Ease.sineInOut,
                Laya.Handler.create(coin, () => orbit(!opposite)),
            );
        };
        orbit(false);
    }

    private refreshBackendBalance(overview: MobileOverview | null): void {
        const account = overview ? this.session.revenue.accountFor(overview, this.session.role) : null;
        this.balanceLabel.text = `后台余额 ¥${Number(account?.totalBalance || 0).toFixed(2)}`;
    }
}

export class ReferenceLevelScreen extends ReferenceBaseScreen {
    private static readonly PAGE_SIZE = 6;
    private readonly nodeLayer = new Laya.Box();
    private readonly pageLabel: Laya.Label;
    private pageStart: number;

    constructor(nav: GameNavigator) {
        super(nav);
        this.showReference("levels");
        this.hit("levels", 4, 5, 68, 70, () => nav.navigate("home"), "back");

        this.pageStart = this.pageStartFor(this.store.state.currentLevel);
        this.nodeLayer.name = "level_nodes";
        this.nodeLayer.width = GameConfig.DESIGN_WIDTH;
        this.nodeLayer.height = GameConfig.DESIGN_HEIGHT;
        this.addChild(this.nodeLayer);

        const pageRect = ReferenceUI.sourceRect("levels", 156, 875, 149, 48);
        this.pageLabel = UIFactory.label("", 18 * pageRect.scale, "#6B2818", pageRect.width, pageRect.height, true);
        this.pageLabel.name = "level_page_label";
        this.pageLabel.pos(pageRect.x, pageRect.y);
        this.pageLabel.stroke = Math.max(1, Math.round(pageRect.scale));
        this.pageLabel.strokeColor = "#FFF1BF";
        this.addChild(this.pageLabel);

        const balance = new BackendBalanceChip(nav);
        balance.pos(520, 34);
        this.addChild(balance);

        this.addPageButton("上一组", 24, () => this.changePage(-ReferenceLevelScreen.PAGE_SIZE));
        this.addPageButton("下一组", 317, () => this.changePage(ReferenceLevelScreen.PAGE_SIZE));
        this.renderPage(nav);
        this.wrapGameplayContent(nav);
    }

    private renderPage(nav: GameNavigator): void {
        this.nodeLayer.removeChildren();
        const end = Math.min(GameConfig.TOTAL_LEVELS, this.pageStart + ReferenceLevelScreen.PAGE_SIZE - 1);
        this.pageLabel.text = `第 ${this.pageStart}-${end} 关`;

        const points = [
            { centerX: 128, centerY: 250 },
            { centerX: 344, centerY: 327 },
            { centerX: 280, centerY: 465 },
            { centerX: 110, centerY: 551 },
            { centerX: 350, centerY: 665 },
            { centerX: 210, centerY: 795 },
        ];

        points.forEach((point, index) => {
            const level = this.pageStart + index;
            if (level <= GameConfig.TOTAL_LEVELS) this.addLevelNode(nav, level, point);
        });
    }

    private addLevelNode(
        nav: GameNavigator,
        level: number,
        point: { centerX: number; centerY: number },
    ): void {
        const rect = ReferenceUI.sourceRect("levels", point.centerX - 70, point.centerY - 70, 140, 170);
        const scale = rect.scale;
        const unlocked = level <= this.store.state.maxUnlocked;
        const completed = !!this.store.state.levels[level]?.completed;
        const current = level === this.store.state.currentLevel;
        const node = new Laya.Box();
        node.name = `level_${level}`;
        node.pos(rect.x, rect.y);
        node.width = rect.width;
        node.height = rect.height;
        node.mouseEnabled = true;

        const centerX = rect.width / 2;
        const centerY = 70 * scale;
        const outer = completed ? "#D88A20" : unlocked ? "#D46B28" : "#5F5049";
        const middle = completed ? "#F4AD2E" : unlocked ? "#EF8B2C" : "#7B6B61";
        const inner = current ? "#F04A3A" : completed ? "#F3A225" : unlocked ? "#E8792A" : "#5D514B";
        node.graphics.drawRoundRect(centerX - 56 * scale, centerY + 22 * scale, 112 * scale, 54 * scale, 22 * scale, 22 * scale, 22 * scale, 22 * scale, outer, "#6B321E", 3 * scale);
        node.graphics.drawRoundRect(centerX - 47 * scale, centerY + 28 * scale, 94 * scale, 36 * scale, 16 * scale, 16 * scale, 16 * scale, 16 * scale, middle, unlocked ? "#FFE199" : "#AA9587", 2 * scale);
        node.graphics.drawCircle(centerX, centerY + 8 * scale, 48 * scale, outer, "#6B321E", 4 * scale);
        node.graphics.drawCircle(centerX, centerY, 43 * scale, middle, unlocked ? "#FFE199" : "#AA9587", 4 * scale);
        node.graphics.drawCircle(centerX, centerY - 5 * scale, 31 * scale, inner, "#A64B20", 2 * scale);

        const number = UIFactory.label(unlocked ? String(level) : "锁", unlocked ? 31 * scale : 23 * scale, unlocked ? "#FFF4C6" : "#E8D0B0", 70 * scale, 62 * scale, true);
        number.pos(centerX - 35 * scale, centerY - 36 * scale);
        number.stroke = Math.max(1, Math.round(2 * scale));
        number.strokeColor = unlocked ? "#8D321E" : "#4D403A";
        number.mouseEnabled = false;
        node.addChild(number);

        const stateText = completed ? "★★★" : current ? "当前关" : unlocked ? "☆☆☆" : "未解锁";
        node.graphics.drawRoundRect(centerX - 50 * scale, 132 * scale, 100 * scale, 28 * scale, 11 * scale, 11 * scale, 11 * scale, 11 * scale, "#FFE6A8F2");
        const state = UIFactory.label(stateText, (completed ? 18 : 14) * scale, completed ? "#F39819" : "#735044", 96 * scale, 25 * scale, true);
        state.pos(centerX - 48 * scale, 133 * scale);
        state.mouseEnabled = false;
        node.addChild(state);

        node.on(Laya.Event.CLICK, node, () => {
            if (!unlocked) {
                nav.toast(`第 ${level} 关尚未解锁`);
                return;
            }
            this.store.selectLevel(level);
            GameAudio.playSfx("level_start", 900);
            nav.navigate("quiz");
        });
        this.nodeLayer.addChild(node);
        const restingY = node.y;
        const bob = (up: boolean) => {
            if (!node.parent || node.destroyed) return;
            Laya.Tween.to(
                node,
                { y: up ? restingY - 6 * scale : restingY + 3 * scale },
                760 + (level % 3) * 120,
                Laya.Ease.sineInOut,
                Laya.Handler.create(node, () => bob(!up)),
            );
        };
        Laya.timer.once((level % ReferenceLevelScreen.PAGE_SIZE) * 90, node, () => bob(true));
    }

    private addPageButton(text: string, sourceX: number, onClick: () => void): void {
        const rect = ReferenceUI.sourceRect("levels", sourceX, 873, 120, 48);
        const button = UIFactory.button(text, rect.width, rect.height, onClick, "cream", 17 * rect.scale);
        button.name = text === "上一组" ? "previous_level_page" : "next_level_page";
        button.pos(rect.x, rect.y);
        this.addChild(button);
    }

    private changePage(offset: number): void {
        const maxStart = this.pageStartFor(GameConfig.TOTAL_LEVELS);
        this.pageStart = Math.max(1, Math.min(maxStart, this.pageStart + offset));
        this.renderPage(this.nav);
    }

    private pageStartFor(level: number): number {
        return Math.floor((Math.max(1, level) - 1) / ReferenceLevelScreen.PAGE_SIZE) * ReferenceLevelScreen.PAGE_SIZE + 1;
    }
}

export class ReferenceWalletScreen extends ReferenceBaseScreen {
    private readonly session = AdFarmSession.instance;
    private readonly balanceLabel = UIFactory.label("¥ --", 58, "#FFF0A3", 540, 82, true);
    private readonly detailLabel = UIFactory.label("正在同步后台收益…", 25, Theme.ink, 560, 220, false);
    private readonly statusLabel = UIFactory.label("", 22, "#8B4B31", 560, 150, false);

    constructor(nav: GameNavigator) {
        super(nav);
        this.showReference("wallet");
        this.hit("wallet", 3, 3, 70, 72, () => nav.navigate("home"), "back");
        this.coverWalletReferenceBody();
        this.buildLiveWallet();
        this.session.on(AdFarmSession.OVERVIEW_CHANGED, this, this.refreshWallet);
        this.refreshWallet(this.session.overview);
        const balance = new BackendBalanceChip(nav);
        balance.pos(520, 34);
        this.addChild(balance);
        this.addChild(new AppTabBar(nav, "wallet"));
    }

    private coverWalletReferenceBody(): void {
        const cover = new Laya.Box();
        cover.pos(16, 250);
        cover.width = 718;
        cover.height = Math.max(760, visibleDesignHeight() - 250 - 112);
        cover.mouseEnabled = false;
        UIFactory.roundRect(cover, 0, 0, cover.width, cover.height, 18, "#FFF7E4", "#D58C3A", 3);
        this.addChild(cover);
    }

    destroy(destroyChild = true): void {
        this.session.off(AdFarmSession.OVERVIEW_CHANGED, this, this.refreshWallet);
        super.destroy(destroyChild);
    }

    private buildLiveWallet(): void {
        const panel = new Laya.Box();
        panel.pos(52, 285);
        panel.width = 646;
        panel.height = 840;
        UIFactory.roundRect(panel, 0, 8, 646, 825, 24, "#8E2E20", "#6C2118", 3);
        UIFactory.roundRect(panel, 0, 0, 646, 825, 24, "#FFF7E4", "#B4472E", 5);
        UIFactory.roundRect(panel, 18, 20, 610, 180, 16, "#E94B31", "#F5B969", 4);
        this.balanceLabel.pos(53, 56);
        panel.addChild(this.balanceLabel);
        const total = UIFactory.label("可提现余额（以后台为准）", 23, "#FFE6B1", 560, 44, true);
        total.pos(43, 20);
        panel.addChild(total);
        this.detailLabel.pos(43, 228);
        this.detailLabel.align = "left";
        this.detailLabel.valign = "top";
        this.detailLabel.leading = 18;
        this.detailLabel.wordWrap = true;
        panel.addChild(this.detailLabel);
        this.statusLabel.pos(43, 448);
        this.statusLabel.align = "left";
        this.statusLabel.valign = "top";
        this.statusLabel.leading = 10;
        this.statusLabel.wordWrap = true;
        panel.addChild(this.statusLabel);
        const bind = UIFactory.button("绑定收款账户", 255, 76, () => this.showPayoutDialog(), "gold", 28);
        bind.pos(43, 630);
        panel.addChild(bind);
        const withdraw = UIFactory.button("申请提现", 255, 76, () => this.withdraw(), "red", 30);
        withdraw.pos(348, 630);
        panel.addChild(withdraw);
        const refresh = UIFactory.button("刷新后台收益", 330, 66, () => this.refreshFromServer(), "green", 27);
        refresh.pos(158, 730);
        panel.addChild(refresh);
        this.addChild(panel);
    }

    private refreshWallet(overview: MobileOverview | null): void {
        const account = overview ? this.session.revenue.accountFor(overview, this.session.role) : null;
        this.balanceLabel.text = `¥ ${Number(account?.totalBalance || 0).toFixed(2)}`;
        this.detailLabel.text = [
            `今日广告收益：¥${Number(account?.dailyEarnings || 0).toFixed(2)}`,
            `今日剩余次数：${Number(account?.availableViews || 0)} 次`,
            `我的邀请码：${account?.inviteCode || "--"}`,
            `当前身份：${this.session.role === "user" ? "普通用户" : "二级代理"}`,
        ].join("\n");
        const pending = overview?.withdrawals?.find(item => item.status === "pending");
        const min = Number(overview?.admin?.minWithdraw || 0);
        this.statusLabel.text = pending
            ? `提现状态：待总站审核\n申请金额：¥${Number(pending.amount || 0).toFixed(2)}`
            : `最低提现金额：¥${min.toFixed(2)}\n${account?.payoutIdentity ? `收款账户：${this.mask(account.payoutIdentity)}` : "尚未绑定支付宝收款账户"}`;
    }

    private async refreshFromServer(): Promise<void> {
        try {
            await this.session.refreshOverview();
            this.nav.toast("后台收益已更新");
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        }
    }

    private async withdraw(): Promise<void> {
        try {
            const account = this.currentAccount();
            if (!account?.payoutIdentity) throw new Error("请先绑定支付宝收款账户");
            await this.session.payout.submitWithdrawal(this.session.role);
            await this.session.refreshOverview();
            this.nav.toast("提现申请已提交，等待总站审核");
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        }
    }

    private showPayoutDialog(): void {
        const current = this.currentAccount() || {};
        const overlay = new Laya.Box();
        overlay.width = this.width;
        overlay.height = this.height;
        overlay.mouseEnabled = true;
        overlay.graphics.drawRect(0, 0, this.width, this.height, "#081B2DEB");
        const panel = UIFactory.panel(650, 690, "绑定支付宝收款账户");
        panel.pos(50, 330);
        overlay.addChild(panel);
        const name = this.payoutInput("真实姓名", current.payeeName || "", 150, panel);
        const account = this.payoutInput("支付宝手机号或邮箱", current.payoutIdentity || current.alipayAccount || "", 270, panel);
        const note = UIFactory.label("收款姓名必须与支付宝实名认证一致；保存后当天是否可提现，以后台规则为准。", 22, "#805039", 540, 95, false);
        note.pos(55, 385);
        note.wordWrap = true;
        panel.addChild(note);
        const save = UIFactory.button("保存账户", 245, 76, async () => {
            try {
                await this.session.payout.saveAccount(this.session.role, {
                    alipayAccount: account.text.trim(),
                    payoutIdentityType: "ALIPAY_LOGON_ID",
                    payoutIdentity: account.text.trim(),
                    payeeName: name.text.trim(),
                });
                await this.session.refreshOverview();
                overlay.destroy(true);
                this.nav.toast("收款账户已保存");
            } catch (error) {
                this.nav.toast(this.errorMessage(error));
            }
        }, "red", 29);
        save.pos(55, 510);
        panel.addChild(save);
        const cancel = UIFactory.button("取消", 245, 76, () => overlay.destroy(true), "cream", 29);
        cancel.pos(350, 510);
        panel.addChild(cancel);
        this.addChild(overlay);
    }

    private payoutInput(prompt: string, value: string, y: number, panel: Laya.Box): Laya.Input {
        const box = new Laya.Box();
        box.pos(55, y);
        box.width = 540;
        box.height = 82;
        UIFactory.roundRect(box, 0, 0, 540, 82, 16, "#FFF9E8", "#D38A43", 3);
        panel.addChild(box);
        const input = new Laya.Input();
        input.prompt = prompt;
        input.promptColor = "#B18A6B";
        input.text = value;
        input.color = Theme.ink;
        input.font = "Microsoft YaHei";
        input.fontSize = 27;
        input.padding = "0,18,0,18";
        input.pos(59, y + 5);
        input.width = 532;
        input.height = 72;
        panel.addChild(input);
        return input;
    }

    private currentAccount(): MobileAccount | undefined {
        return this.session.overview ? this.session.revenue.accountFor(this.session.overview, this.session.role) : undefined;
    }

    private mask(value: string): string {
        if (value.length <= 5) return value;
        return `${value.slice(0, 3)}****${value.slice(-3)}`;
    }

    private errorMessage(error: unknown): string {
        return error instanceof Error ? error.message : "操作失败，请稍后重试";
    }
}

export class ReferenceRankScreen extends ReferenceBaseScreen {
    constructor(nav: GameNavigator) {
        super(nav);
        this.showReference("rank");
        this.hit("rank", 3, 3, 70, 72, () => nav.navigate("home"), "back");
        this.hit("rank", 10, 205, 130, 58, () => nav.toast("已切换到每日榜"), "daily");
        this.hit("rank", 140, 205, 132, 58, () => nav.toast("已切换到每周榜"), "weekly");
        this.hit("rank", 272, 205, 155, 58, () => nav.toast("已切换到总排行"), "total");
        const balance = new BackendBalanceChip(nav);
        balance.pos(520, 34);
        this.addChild(balance);
        this.wrapGameplayContent(nav);
    }
}

export class ReferenceQuizScreen extends ReferenceBaseScreen {
    private answered = false;
    private adBusy = false;
    private readonly question: PictureQuestion;
    private readonly gameplayContent: Laya.Box;
    private readonly rewardAds = new RewardAdBridge();

    constructor(nav: GameNavigator) {
        super(nav);
        this.question = questionForLevel(this.store.state.currentLevel);
        this.showReference("picture");
        this.paintQuestion(this.question);
        this.hit("picture", 2, 2, 70, 72, () => nav.navigate("levels"), "back");
        this.hit("picture", 320, 55, 65, 80, () => void this.unlockWithAd("hint"), "hint");
        this.hit("picture", 390, 55, 65, 80, () => void this.unlockWithAd("help"), "help");
        const balance = new BackendBalanceChip(nav);
        balance.pos(520, 34);
        this.addChild(balance);
        this.gameplayContent = this.wrapGameplayContent(nav);
    }

    private async unlockWithAd(kind: "hint" | "help"): Promise<void> {
        if (this.adBusy || this.answered) return;
        const session = AdFarmSession.instance;
        const tasks = session.role === "secondaryAgent" ? session.overview?.secondaryAgentTasks : session.overview?.tasks;
        const task = tasks?.[0] || { taskId: `quiz-${kind}`, sourceKey: "pangle" as const, title: kind === "hint" ? "答题提示" : "答题帮助" };

        if (!this.rewardAds.isMockMode() && !tasks?.length) {
            this.nav.toast("后台暂未配置提示广告位");
            return;
        }

        this.adBusy = true;
        const ad = this.rewardAds.isMockMode() ? this.createMockAd(kind) : null;
        try {
            const unlocked = await this.rewardAds.showUnlock(task, message => {
                if (ad) ad.status.text = message;
                else this.nav.toast(message);
            });
            if (!unlocked) {
                this.nav.toast("请完整观看广告后再使用此功能");
                return;
            }
            GameAudio.playSfx(kind === "hint" ? "prop_hint" : "prop_help", 650);
            this.nav.toast(kind === "hint"
                ? `提示：答案是 ${this.question.answer}`
                : `帮助：${this.question.explanation}`);
        } catch (error) {
            this.nav.toast(error instanceof Error ? error.message : "广告播放失败，请稍后重试");
        } finally {
            ad?.overlay.destroy(true);
            this.adBusy = false;
        }
    }

    private createMockAd(kind: "hint" | "help"): { overlay: Laya.Box; status: Laya.Label } {
        const overlay = new Laya.Box();
        overlay.name = "local_rewarded_ad";
        overlay.width = GameConfig.DESIGN_WIDTH;
        overlay.height = visibleDesignHeight() - 112;
        overlay.mouseEnabled = true;
        overlay.graphics.drawRect(0, 0, overlay.width, overlay.height, "#061B2DDD");

        const card = new Laya.Box();
        card.pos(70, Math.max(190, (overlay.height - 430) / 2));
        card.width = 610;
        card.height = 390;
        UIFactory.roundRect(card, 0, 0, 610, 390, 28, "#FFF2D2", "#E59A3C", 6);
        const badge = UIFactory.label("AD", 26, "#FFF8D5", 92, 52, true);
        badge.pos(259, 42);
        badge.bgColor = "#E94B31";
        card.addChild(badge);
        const title = UIFactory.label(kind === "hint" ? "观看广告解锁答案提示" : "观看广告解锁答题帮助", 34, "#6B2818", 540, 62, true);
        title.pos(35, 118);
        card.addChild(title);
        const status = UIFactory.label("激励广告准备中…", 25, "#9A4A2C", 520, 90, true);
        status.pos(45, 205);
        status.wordWrap = true;
        card.addChild(status);
        const note = UIFactory.label("本地测试广告 · 完整播放后自动解锁", 19, "#8B735F", 520, 48, true);
        note.pos(45, 315);
        card.addChild(note);
        overlay.addChild(card);
        this.addChild(overlay);
        return { overlay, status };
    }

    private paintQuestion(question: PictureQuestion): void {
        // Replace the complete content area. The reference image is only used for
        // the surrounding frame; no original question art or button can leak through.
        this.solidRect("picture", 0, 150, 462, 610, "#E84B31");
        this.questionPanel(question);
        this.optionButtons(question);
    }

    private questionPanel(question: PictureQuestion): void {
        this.roundRect("picture", 17, 190, 428, 300, 20, "#8E2E20", "#6D2118", 3);
        this.roundRect("picture", 17, 182, 428, 300, 20, "#FFF2D2", "#A63A25", 5);
        this.roundRect("picture", 28, 194, 406, 274, 14, "#FFF9E8", "#E0B172", 2);

        this.roundRect("picture", 92, 164, 278, 62, 15, "#9B321F", "#702417", 3);
        this.roundRect("picture", 92, 157, 278, 62, 15, "#F8D894", "#B94B2C", 4);
        this.roundRect("picture", 99, 162, 264, 51, 12, "#FFE8B5", "#FFF4D0", 2);
        this.label("picture", "看图猜成语", 28, "#6B2818", 104, 163, 254, 48);

        this.clueTile(question.clues[0], question.imageKeys[0], 43, 230);
        this.clueTile(question.clues[1], question.imageKeys[1], 245, 230);

        const progress = Math.max(0.08, Math.min(1, this.store.state.currentLevel / GameConfig.TOTAL_LEVELS));
        this.roundRect("picture", 43, 425, 275, 24, 12, "#762C27", "#E8B15F", 3);
        this.roundRect("picture", 48, 430, 265 * progress, 14, 7, "#58C83C", "#9AF070", 1);
        this.label(
            "picture",
            `第 ${this.store.state.currentLevel} / ${GameConfig.TOTAL_LEVELS} 题`,
            14,
            "#8A4027",
            325,
            418,
            100,
            38,
        );
    }

    private clueTile(caption: string, imageKey: IconKey, x: number, y: number): void {
        this.roundRect("picture", x, y, 174, 166, 14, "#FFF4D9", "#D89852", 2);
        const imageRect = ReferenceUI.sourceRect("picture", x + 13, y + 8, 148, 148);
        const image = new Laya.Image(ClueImages.forKey(imageKey));
        image.name = `clue_${caption}`;
        image.pos(imageRect.x, imageRect.y);
        image.width = imageRect.width;
        image.height = imageRect.height;
        image.mouseEnabled = false;
        this.addChild(image);
    }

    private optionButtons(question: PictureQuestion): void {
        const positions = [
            { x: 31, y: 520 },
            { x: 239, y: 520 },
            { x: 31, y: 607 },
            { x: 239, y: 607 },
        ];

        question.options.forEach((option, index) => {
            const position = positions[index];
            const rect = ReferenceUI.sourceRect("picture", position.x, position.y, 192, 65);
            const button = UIFactory.button(
                option,
                rect.width,
                rect.height,
                () => this.finish(option === question.answer),
                "gold",
                Math.max(28, 20 * rect.scale),
            );
            button.name = `option_${index}`;
            button.pos(rect.x, rect.y);
            const restingY = rect.y;
            button.y += 22;
            button.alpha = 0;
            this.addChild(button);
            Laya.Tween.to(button, { y: restingY, alpha: 1 }, 300, Laya.Ease.backOut, null, index * 85);
        });
    }

    private solidRect(screen: ReferenceScreenName, x: number, y: number, width: number, height: number, fill: string): void {
        const rect = ReferenceUI.sourceRect(screen, x, y, width, height);
        const box = new Laya.Box();
        box.pos(rect.x, rect.y);
        box.width = rect.width;
        box.height = rect.height;
        box.mouseEnabled = false;
        box.graphics.drawRect(0, 0, rect.width, rect.height, fill);
        this.addChild(box);
    }

    private roundRect(
        screen: ReferenceScreenName,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
        fill: string,
        line: string,
        lineWidth: number,
    ): void {
        const rect = ReferenceUI.sourceRect(screen, x, y, width, height);
        const box = new Laya.Box();
        box.pos(rect.x, rect.y);
        box.width = rect.width;
        box.height = rect.height;
        box.mouseEnabled = false;
        box.graphics.drawRoundRect(
            0,
            0,
            rect.width,
            rect.height,
            radius * rect.scale,
            radius * rect.scale,
            radius * rect.scale,
            radius * rect.scale,
            fill,
            line,
            Math.max(1, lineWidth * rect.scale),
        );
        this.addChild(box);
    }

    private label(screen: ReferenceScreenName, text: string, fontSize: number, color: string, x: number, y: number, width: number, height: number): void {
        const rect = ReferenceUI.sourceRect(screen, x, y, width, height);
        const label = UIFactory.label(text, fontSize * rect.scale, color, rect.width, rect.height, true);
        label.pos(rect.x, rect.y);
        label.mouseEnabled = false;
        label.wordWrap = true;
        label.stroke = Math.max(1, Math.round(rect.scale));
        label.strokeColor = "#FFF4C9";
        this.addChild(label);
    }

    private async finish(correct: boolean): Promise<void> {
        if (this.answered) return;
        this.answered = true;
        GameAudio.playSfx(correct ? "success" : "failure", correct ? 1500 : 1700);
        let backendReward = 0;
        let alreadyClaimed = false;
        if (correct) {
            this.store.completeCurrentLevel();
            try {
                const result = await AdFarmSession.instance.claimQuizReward(this.store.state.currentLevel);
                backendReward = Number(result.reward || 0);
                alreadyClaimed = !!result.alreadyClaimed;
                if (backendReward > 0) Laya.timer.once(520, this, () => GameAudio.playSfx("coin", 700));
                this.nav.toast(alreadyClaimed ? "本关真实奖励已领取" : `答对奖励 +¥${backendReward.toFixed(2)}，已计入可提现余额`);
            } catch (error) {
                this.nav.toast(error instanceof Error ? error.message : "答题奖励入账失败，请稍后重试");
            }
        }
        this.gameplayContent.addChild(new ReferenceResultOverlay(this.nav, correct, this.question, backendReward, alreadyClaimed));
    }
}

class ReferenceResultOverlay extends Laya.Box {
    private readonly store = GameStore.instance;
    private readonly rewardAds = new RewardAdBridge();
    private claiming = false;

    constructor(nav: GameNavigator, correct: boolean, question: PictureQuestion, backendReward = 0, alreadyClaimed = false) {
        super();
        this.width = GameConfig.DESIGN_WIDTH;
        this.height = GameConfig.DESIGN_HEIGHT;
        this.mouseEnabled = true;

        const screen: ReferenceScreenName = correct ? "success" : "failure";
        this.addChild(ReferenceUI.createScreen(screen));
        this.paintAnswer(screen, question, backendReward, alreadyClaimed);
        const balance = new BackendBalanceChip(nav);
        balance.pos(520, 34);
        this.addChild(balance);
        ReferenceUI.addHit(this, screen, 2, 2, 70, 72, () => nav.navigate("levels"), "back");

        if (correct) {
            FestiveMotion.celebrate(this);
            this.addClaimButton(nav, screen);
            ReferenceUI.addHit(this, screen, 45, 750, 165, 70, () => this.next(nav), "next");
            ReferenceUI.addHit(this, screen, 240, 750, 165, 70, () => nav.toast("分享卡片已准备好"), "share");
        } else {
            ReferenceUI.addHit(this, screen, 45, 635, 170, 75, () => nav.navigate("quiz"), "retry");
            ReferenceUI.addHit(this, screen, 235, 635, 175, 75, () => {
                this.store.skipCurrentLevel();
                nav.navigate("quiz");
            }, "skip");
        }
    }

    private next(nav: GameNavigator): void {
        if (this.store.state.currentLevel >= GameConfig.TOTAL_LEVELS) {
            nav.navigate("levels");
            return;
        }
        this.store.goNextLevel();
        GameAudio.playSfx("level_start", 800);
        nav.navigate("quiz");
    }

    private paintAnswer(screen: ReferenceScreenName, question: PictureQuestion, backendReward: number, alreadyClaimed: boolean): void {
        if (screen === "success") {
            this.cover(screen, 48, 250, 366, 480, "#FFF7E4");
            this.label(screen, "答题成功", 34, "#A52B1F", 72, 285, 318, 58);
            this.label(screen, question.answer, 31, "#6B2818", 72, 355, 318, 54);
            this.label(screen, alreadyClaimed ? "本关真实奖励已领取" : `+¥${backendReward.toFixed(2)} 可提现余额`, 25, "#E5412D", 72, 425, 318, 54);
            this.label(screen, "答题奖励已实时入账；观看广告可再领取额外红包", 19, "#76503A", 82, 500, 298, 90);
            return;
        }
        // Replace the whole text area inside the parchment card so the static
        // first-question answer from the reference image never shows through.
        this.cover(screen, 30, 400, 402, 230, "#FFF7E4");
        this.label(screen, "可惜！答错了！", 27, "#5F2016", 70, 410, 322, 46);
        this.label(screen, question.answer, 24, "#7A2519", 80, 466, 302, 40);
        this.label(screen, question.explanation, 17, "#6B2B18", 62, 516, 338, 92);
    }

    private addClaimButton(nav: GameNavigator, screen: ReferenceScreenName): void {
        const rect = ReferenceUI.sourceRect(screen, 70, 640, 320, 82);
        const button = UIFactory.button("观看广告领红包", rect.width, rect.height - 8, () => this.claimReward(nav), "green", 25 * rect.scale);
        button.name = "claim_server_reward";
        button.pos(rect.x, rect.y);
        this.addChild(button);
    }

    private async claimReward(nav: GameNavigator): Promise<void> {
        if (this.claiming) return;
        const session = AdFarmSession.instance;
        const overview = session.overview;
        const tasks = session.role === "secondaryAgent" ? overview?.secondaryAgentTasks : overview?.tasks;
        const task = tasks?.[0];
        if (!task) {
            nav.toast("后台暂未配置可领取的广告任务");
            return;
        }
        this.claiming = true;
        try {
            const status = await this.rewardAds.show(task, message => nav.toast(message));
            nav.toast(status.verified || status.sessionStatus === "settled" ? "收益已更新，请到我的红包查看" : "奖励确认中，请稍后刷新红包余额");
        } catch (error) {
            nav.toast(error instanceof Error ? error.message : "广告领取失败，请稍后重试");
        } finally {
            this.claiming = false;
        }
    }

    private cover(screen: ReferenceScreenName, x: number, y: number, width: number, height: number, fill: string): void {
        const rect = ReferenceUI.sourceRect(screen, x, y, width, height);
        const box = new Laya.Box();
        box.pos(rect.x, rect.y);
        box.width = rect.width;
        box.height = rect.height;
        box.mouseEnabled = false;
        box.graphics.drawRoundRect(
            0,
            0,
            rect.width,
            rect.height,
            12 * rect.scale,
            12 * rect.scale,
            12 * rect.scale,
            12 * rect.scale,
            fill,
            "#D58C3A",
            Math.max(1, 2 * rect.scale),
        );
        this.addChild(box);
    }

    private label(screen: ReferenceScreenName, text: string, fontSize: number, color: string, x: number, y: number, width: number, height: number): void {
        const rect = ReferenceUI.sourceRect(screen, x, y, width, height);
        const label = UIFactory.label(text, fontSize * rect.scale, color, rect.width, rect.height, true);
        label.pos(rect.x, rect.y);
        label.wordWrap = true;
        label.mouseEnabled = false;
        this.addChild(label);
    }
}
